import React, { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from 'src/contexts/userContext';
import { UserType } from 'src/interfaces/UserTypes';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Snackbar,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormattedMessage, useIntl } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import { AdminUser, CommentAuthorType, TicketComment } from 'src/interfaces/TicketTypes';
import DocumentList from 'src/components/DocumentList';
import CommentFormatToolbar from './CommentFormatToolbar';
import { isSelectableAdmin } from 'src/utils/adminFilters';
import {
  renderCommentBody,
  handleListEnter,
  inlinedDocumentIds
} from './commentMarkdown';
import { usePasteImage, PendingPaste } from 'src/hooks/usePasteImage';

interface CommentThreadProps {
  ticketId: number;
  comments: TicketComment[];
  onCommentAdded: () => void;
  // Staff who may be @mentioned.
  //
  // INVARIANT — a client or partner must NEVER receive the admin roster here.
  // The admin view passes /GetAdminUsers (admin-gated, UserType != 2 => 401).
  // The partner view passes GetMyTicketDetail.participants, which is derived from
  // VisibleCommentsFor and therefore contains ONLY admins who posted a reply the
  // partner can actually see — an admin who wrote just an internal note never
  // appears. Do not "simplify" the partner path to fetch /GetAdminUsers.
  adminUsers?: AdminUser[];
  // Non-admin people on this ticket — its creator plus anyone who commented.
  // Resolves their display names on their own comments. NOT the mention list:
  // the admin picker uses mentionableCustomers below, which is wider. The partner
  // view leaves this empty and mentions staff via adminUsers.
  participants?: AdminUser[];
  // ADMIN ONLY — non-staff people the admin may @mention: everyone with access to
  // this ticket's installation, plus the people already on it. Comes from
  // GetTicketDetail, which 401s a non-admin.
  //
  // INVARIANT — a client or partner must NEVER receive this list. It is the
  // installation's whole user roster, and handing it to a customer would leak the
  // names of every other customer and partner on that site. Their picker is
  // restricted to people who have appeared in the comments, via adminUsers above.
  mentionableCustomers?: AdminUser[];
  // Admin-only: offer the "internal note" toggle, which posts a comment the
  // backend hides from clients and partners.
  canPostInternalNote?: boolean;
}

function CommentThread({
  ticketId,
  comments,
  onCommentAdded,
  adminUsers = [],
  participants = [],
  mentionableCustomers = [],
  canPostInternalNote = false
}: CommentThreadProps) {
  const intl = useIntl();
  const userCtx = useContext(UserContext);
  const currentUserId = userCtx?.currentUser?.id;
  const isAdmin = userCtx?.currentUser?.userType === UserType.admin;
  const [body, setBody] = useState('');
  const [internalNote, setInternalNote] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteCandidateId, setDeleteCandidateId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [mentionedIds, setMentionedIds] = useState<number[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const commentInputRef = useRef<HTMLInputElement | null>(null);
  const editInputRef    = useRef<HTMLInputElement | null>(null);

  const [pendingPastes, setPendingPastes] = useState<PendingPaste[]>([]);
  const [pasteError, setPasteError] = useState<'tooLarge' | 'uploadFailed' | null>(null);
  // People the author @mentioned whom the backend refused to notify.
  const [droppedMentionNames, setDroppedMentionNames] = useState<string[]>([]);

  const pendingPastesRef = useRef<PendingPaste[]>([]);
  pendingPastesRef.current = pendingPastes;

  useEffect(() => {
    return () => {
      pendingPastesRef.current.forEach((p) => window.URL.revokeObjectURL(p.blobUrl));
    };
  }, []);

  const handlePasteNewComment = usePasteImage({
    mode: 'deferred',
    textareaRef: commentInputRef,
    value: body,
    onChange: setBody,
    onError: setPasteError,
    onBufferedPaste: (p) => setPendingPastes((prev) => [...prev, p])
  });

  const handlePasteEditComment = usePasteImage({
    mode: 'immediate',
    textareaRef: editInputRef,
    value: editBody,
    onChange: setEditBody,
    onError: setPasteError,
    ticketId,
    ticketCommentId: editingId ?? undefined
  });

  // Everyone whose display name we can resolve: staff, plus the non-admin people on
  // this ticket. Also used for author labels — without the participants a partner's
  // own comment renders to an admin as "User #12".
  const knownUsers = [...adminUsers, ...participants, ...mentionableCustomers];

  // The Assignee deny-list governs where WORK is routed, so it applies to the staff
  // list an admin sees, keeping the two pickers identical. It is deliberately NOT
  // applied on the partner side: a partner must always be able to reply to whoever
  // actually answered them, even someone who is never assigned tickets. Narrowing
  // that list would strand a live customer conversation.
  const staffPool = isAdmin ? adminUsers.filter(isSelectableAdmin) : adminUsers;

  // Guests come first so the partner who opened the ticket is the easiest person to
  // reach, rather than being buried under a long staff list. The backend already
  // ranks the people actually on the ticket ahead of the rest of the installation
  // roster, so that stays true now the list is the whole site.
  //
  // They are withheld entirely on an internal note: it is invisible to them and the
  // backend refuses non-admin mentions on one, so offering them would insert a token
  // that notifies nobody. Yourself is withheld too — the backend drops self-mentions,
  // so listing you would be a menu entry that provably does nothing.
  const mentionPool = [
    ...(internalNote ? [] : mentionableCustomers.map((u) => ({ ...u, isGuest: true }))),
    ...staffPool.map((u) => ({ ...u, isGuest: false }))
  ].filter((u) => u.id !== currentUserId);

  // NOT truncated. This list used to end in .slice(0, 8), which silently hid every
  // admin past the eighth on a bare "@" — the picker looked complete while being
  // anything but. Every selectable admin must be reachable here; the Paper below
  // scrolls, so length is a layout concern, not a correctness one.
  const mentionCandidates = mentionQuery === null
    ? []
    : mentionPool.filter((u) =>
        (u.name ?? '').toLowerCase().includes(mentionQuery.toLowerCase()) &&
        !mentionedIds.includes(u.id)
      );

  // Is "@Name" still present in the text as a WHOLE token?
  //
  // A plain text.includes('@' + name) is not enough: "@Ann" is a prefix of
  // "@Anna Weber", so picking the customer Ann and then editing the token to the
  // admin Anna Weber left Ann's id in the list and quietly emailed the customer a
  // comment the author believed was addressed to a colleague. Requiring the next
  // character to be a non-letter/non-digit stops a short name matching inside a
  // longer one.
  const hasMentionToken = (text: string, name: string) => {
    // An empty name makes the token a bare "@", which matches almost any comment
    // text and would keep this user's id attached to a comment never addressed to
    // them. The backend already filters nameless users out of both mention lists;
    // this is the second line of defence, since being wrong here mails a stranger.
    if (!name?.trim()) return false;
    const token = `@${name}`;
    for (let i = text.indexOf(token); i !== -1; i = text.indexOf(token, i + 1)) {
      const next = text[i + token.length];
      if (next === undefined || !/[\p{L}\p{N}]/u.test(next)) return true;
    }
    return false;
  };

  const detectMention = (text: string, cursor: number) => {
    const upToCursor = text.slice(0, cursor);
    const atIdx = upToCursor.lastIndexOf('@');
    if (atIdx === -1) return null;
    const between = upToCursor.slice(atIdx + 1);
    if (/\s/.test(between)) return null;
    const prevChar = atIdx === 0 ? ' ' : upToCursor[atIdx - 1];
    if (!/\s|^$/.test(prevChar) && atIdx !== 0) return null;
    return { atIdx, query: between };
  };

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const text    = e.target.value;
    const cursor  = e.target.selectionStart ?? text.length;
    setBody(text);
    const match = detectMention(text, cursor);
    setMentionQuery(match ? match.query : null);

    // Drop mentioned IDs whose display names no longer appear in the body
    setMentionedIds((prev) =>
      prev.filter((uid) => {
        const u = knownUsers.find((ku) => ku.id === uid);
        return u ? hasMentionToken(text, u.name) : false;
      })
    );
  };

  const handleSelectMention = (userId: number, userName: string) => {
    const input  = commentInputRef.current;
    const cursor = input?.selectionStart ?? body.length;
    const match  = detectMention(body, cursor);
    if (!match) return;
    const before = body.slice(0, match.atIdx);
    const after  = body.slice(cursor);
    const token  = `@${userName} `;
    const next   = `${before}${token}${after}`;
    setBody(next);
    setMentionedIds((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
    setMentionQuery(null);
    const caret = before.length + token.length;
    setTimeout(() => {
      input?.focus();
      input?.setSelectionRange(caret, caret);
    }, 0);
  };

  const ALLOWED_TYPES = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf',
    'video/mp4', 'video/quicktime', 'video/webm'
  ];
  const MAX_FILE_SIZE = 100 * 1024 * 1024;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (let i = 0; i < files.length; i++) {
      if (!ALLOWED_TYPES.includes(files[i].type) || files[i].size > MAX_FILE_SIZE) return;
    }
    setSelectedFiles((prev) => [...prev, ...Array.from(files)]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sorted = [...comments].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const startEdit = (comment: TicketComment) => {
    setEditingId(comment.id);
    setEditBody(comment.body);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBody('');
  };

  const confirmDelete = async () => {
    if (deleteCandidateId == null) return;
    const id = deleteCandidateId;
    setDeletingId(id);
    try {
      await axiosConfig.delete('/DeleteTicketComment', { params: { id } });
      setDeleteCandidateId(null);
      if (editingId === id) {
        setEditingId(null);
        setEditBody('');
      }
      onCommentAdded();
    } finally {
      setDeletingId(null);
    }
  };

  const saveEdit = async (commentId: number) => {
    if (!editBody.trim()) return;
    setSavingEdit(true);
    try {
      await axiosConfig.post('/UpdateTicketComment', {
        id: commentId,
        body: editBody
      });
      setEditingId(null);
      setEditBody('');
      onCommentAdded();
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSubmit = async () => {
    if (!body.trim() && selectedFiles.length === 0 && pendingPastes.length === 0) return;
    setSubmitting(true);

    try {
      let commentId: number | undefined;
      let finalBody = body;
      // An internal note ALWAYS creates the comment, even with an empty body:
      // without a commentId the file upload below falls back to ticket scope,
      // which is visible to the client/partner — the opposite of "internal".
      if (body.trim() || internalNote) {
        // Keep only mentions whose token still survives in the body. Non-staff are
        // dropped from an internal note even if they were picked before the toggle
        // was switched on — the backend refuses them there anyway, so sending them
        // would only produce a mention the server silently discards. Note the
        // "@Name" text stays visible in the note; only the notification is dropped.
        //
        // Phrased as "not staff" rather than "is a guest": the guest pool is
        // mentionableCustomers (the whole installation roster), NOT participants, and
        // keying on a specific guest list is what let this drift once already. The
        // backend's rule on an internal note is literally "admins only", so mirror
        // that instead of enumerating the population it excludes.
        //
        // Tested against adminUsers, NOT staffPool: staffPool is adminUsers minus the
        // Assignee deny-list, which governs where WORK is routed and says nothing about
        // who may be notified. Using it here would silently drop a mention of a
        // deny-listed admin — someone the backend would have delivered to.
        const activeMentionedIds = mentionedIds.filter((uid) => {
          if (internalNote && !adminUsers.some((a) => a.id === uid)) return false;
          const u = knownUsers.find((ku) => ku.id === uid);
          return u ? hasMentionToken(body, u.name) : false;
        });
        const res = await axiosConfig.post('/AddTicketComment', {
          ticketId,
          body,
          mentionedUserIds: activeMentionedIds,
          // Ignored by the backend for non-admins, which force Human.
          authorType: internalNote
            ? CommentAuthorType.InternalNote
            : CommentAuthorType.Human
        });
        commentId = res.data?.id;

        // The backend echoes back the mentions it ACCEPTED. It silently refuses a
        // recipient who can no longer reach the ticket (installation access revoked
        // since they commented), so without this diff the author reads a plain
        // success and assumes the customer was notified when nobody was.
        const accepted: number[] = res.data?.mentionedUserIds ?? activeMentionedIds;
        const dropped = activeMentionedIds.filter((uid) => !accepted.includes(uid));
        setDroppedMentionNames(
          dropped
            .map((uid) => knownUsers.find((ku) => ku.id === uid)?.name)
            .filter((n): n is string => !!n)
        );
      }

      // Upload buffered pasted images, then rewrite blob: URLs to /DownloadDocument URLs
      if (pendingPastes.length > 0 && commentId) {
        setUploading(true);
        let bodyChanged = false;
        for (const paste of pendingPastes) {
          const formData = new FormData();
          formData.append('file', paste.blob);
          try {
            const res = await axiosConfig.post('/UploadDocument', formData, {
              // Attach to the comment only. Sending ticketId too would make the
              // backend store it at ticket scope, leaking it into the ticket's
              // Description attachment list.
              params: { scope: 0, ticketCommentId: commentId },
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            const docId = res.data?.id;
            if (docId) {
              const realUrl = `/DownloadDocument?id=${docId}`;
              if (finalBody.includes(paste.blobUrl)) {
                finalBody = finalBody.split(paste.blobUrl).join(realUrl);
                bodyChanged = true;
              }
            }
          } catch (err: any) {
            console.warn('[Paste] Upload failed:', err?.response?.data || err?.message);
            setPasteError('uploadFailed');
          }
          window.URL.revokeObjectURL(paste.blobUrl);
        }
        if (bodyChanged) {
          try {
            await axiosConfig.post('/UpdateTicketComment', { id: commentId, body: finalBody });
          } catch (err: any) {
            console.warn('[Paste] Body rewrite failed:', err?.response?.data || err?.message);
          }
        }
        setUploading(false);
      }

      if (selectedFiles.length > 0) {
        setUploading(true);
        for (const file of selectedFiles) {
          // Belt and braces: never publish an internal note's attachment at
          // ticket scope if the note itself failed to post.
          if (internalNote && !commentId) break;
          const formData = new FormData();
          formData.append('file', file);
          try {
            await axiosConfig.post('/UploadDocument', formData, {
              // Attach to the comment when one was created; otherwise (file-only
              // submit, no comment body) fall back to ticket scope. Never send
              // both — that duplicates the file into the Description list. The
              // ticket-scope fallback is customer-visible, so it must never be
              // reached for an internal note (guarded above and again below).
              params: commentId
                ? { scope: 0, ticketCommentId: commentId }
                : { scope: 0, ticketId },
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (err: any) {
            console.warn(`[Documents] Upload failed for ${file.name}:`, err?.response?.data || err?.message);
          }
        }
        setUploading(false);
      }

      setBody('');
      setInternalNote(false);
      setMentionedIds([]);
      setMentionQuery(null);
      setSelectedFiles([]);
      setPendingPastes([]);
      setRefreshKey((k) => k + 1);
      onCommentAdded();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <FormattedMessage id="comments" defaultMessage="Comments" />
        }
      />
      <Divider />
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sorted.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage
              id="noComments"
              defaultMessage="No comments yet."
            />
          </Typography>
        )}

        {sorted.map((comment) => {
          const isAi = comment.authorType === CommentAuthorType.AiAgent;
          // Only admins ever receive these; the backend strips them otherwise.
          const isInternal =
            comment.authorType === CommentAuthorType.InternalNote;
          const canEdit = !isAi && currentUserId != null && comment.authorId === currentUserId;
          const isEditing = editingId === comment.id;
          return (
            <Box
              key={comment.id}
              sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isAi ? 'primary.main' : 'grey.500'
                }}
              >
                {isAi ? (
                  <SmartToyIcon fontSize="small" />
                ) : (
                  <PersonIcon fontSize="small" />
                )}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}
                >
                  <Typography variant="subtitle2">
                    {isAi ? 'AI' : (knownUsers.find(u => u.id === comment.authorId)?.name ?? `User #${comment.authorId ?? '?'}`)}
                  </Typography>
                  {isInternal && (
                    <Chip
                      size="small"
                      color="warning"
                      label={intl.formatMessage({
                        id: 'internalNote',
                        defaultMessage: 'Internal note'
                      })}
                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                    />
                  )}
                  <Typography variant="caption" color="text.disabled">
                    {new Date(comment.createdAt).toLocaleString()}
                  </Typography>
                  {comment.editedAt && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                      <FormattedMessage
                        id="commentEdited"
                        defaultMessage="(edited {time})"
                        values={{ time: new Date(comment.editedAt).toLocaleString() }}
                      />
                    </Typography>
                  )}
                  {canEdit && !isEditing && (
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                      <Tooltip title={intl.formatMessage({ id: 'edit', defaultMessage: 'Edit' })}>
                        <IconButton
                          size="small"
                          onClick={() => startEdit(comment)}
                          aria-label={intl.formatMessage({ id: 'edit', defaultMessage: 'Edit' })}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={intl.formatMessage({ id: 'delete', defaultMessage: 'Delete' })}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteCandidateId(comment.id)}
                          disabled={deletingId === comment.id}
                          aria-label={intl.formatMessage({ id: 'delete', defaultMessage: 'Delete' })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
                {isEditing ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                    <CommentFormatToolbar
                      textareaRef={editInputRef}
                      value={editBody}
                      onChange={setEditBody}
                      disabled={savingEdit}
                    />
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      onKeyDown={(e) => handleListEnter(e, editBody, setEditBody)}
                      onPaste={handlePasteEditComment}
                      inputRef={editInputRef}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={cancelEdit}
                        disabled={savingEdit}
                      >
                        <FormattedMessage id="cancel" defaultMessage="Cancel" />
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => saveEdit(comment.id)}
                        disabled={savingEdit || !editBody.trim()}
                      >
                        <FormattedMessage id="save" defaultMessage="Save" />
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  renderCommentBody(comment.body)
                )}
                <DocumentList
                  ticketCommentId={comment.id}
                  refreshKey={refreshKey}
                  canDelete
                  deleteOwnerId={isAdmin ? undefined : currentUserId ?? -1}
                  protectedIds={inlinedDocumentIds(comment.body)}
                />
              </Box>
            </Box>
          );
        })}

        <Divider />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <CommentFormatToolbar
              textareaRef={commentInputRef}
              value={body}
              onChange={setBody}
              disabled={submitting || uploading}
            />
            <Typography variant="caption" color="text.disabled">
              <FormattedMessage
                id="commentMarkdownHint"
                defaultMessage="Markdown: **bold**, #, ##"
              />
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              placeholder={intl.formatMessage({
                id: 'mentionPlaceholder',
                defaultMessage: 'Type @ to mention a user'
              })}
              value={body}
              onChange={handleBodyChange}
              onKeyDown={(e) => handleListEnter(e, body, setBody)}
              onPaste={handlePasteNewComment}
              inputRef={commentInputRef}
            />
            <Popper
              open={mentionQuery !== null && mentionCandidates.length > 0}
              anchorEl={commentInputRef.current}
              placement="top-start"
              style={{ zIndex: 1300 }}
            >
              <ClickAwayListener onClickAway={() => setMentionQuery(null)}>
                <Paper elevation={4} sx={{ minWidth: 200, maxHeight: 240, overflowY: 'auto' }}>
                  <MenuList dense>
                    {mentionCandidates.map((u) => (
                      <MenuItem
                        key={u.id}
                        onClick={() => handleSelectMention(u.id, u.name)}
                        sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}
                      >
                        {u.name}
                        {u.isGuest && (
                          <Chip
                            size="small"
                            color="info"
                            variant="outlined"
                            label={intl.formatMessage(
                              u.userType === UserType.partner
                                ? { id: 'mentionPartnerTag',  defaultMessage: 'Partner' }
                                : { id: 'mentionCustomerTag', defaultMessage: 'Customer' }
                            )}
                          />
                        )}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Paper>
              </ClickAwayListener>
            </Popper>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignSelf: 'flex-end' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,video/mp4,video/quicktime,video/webm"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting || uploading}
              >
                <AttachFileIcon fontSize="small" />
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || uploading || (!body.trim() && selectedFiles.length === 0)}
              >
                <FormattedMessage id="addComment" defaultMessage="Add" />
              </Button>
            </Box>
          </Box>
          {canPostInternalNote && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={internalNote}
                  onChange={(e) => setInternalNote(e.target.checked)}
                  disabled={submitting || uploading}
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  <FormattedMessage
                    id="internalNoteHint"
                    defaultMessage="Internal note — not visible to the client or partner"
                  />
                </Typography>
              }
            />
          )}
          {selectedFiles.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedFiles.map((f, i) => (
                <Chip
                  key={i}
                  label={f.name}
                  size="small"
                  onDelete={() => setSelectedFiles((prev) => prev.filter((_, idx) => idx !== i))}
                />
              ))}
            </Box>
          )}
          {uploading && <LinearProgress />}
        </Box>
      </CardContent>
      <Dialog
        open={deleteCandidateId !== null}
        onClose={() => deletingId === null && setDeleteCandidateId(null)}
      >
        <DialogTitle>
          <FormattedMessage id="deleteComment" defaultMessage="Delete comment" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <FormattedMessage
              id="deleteCommentConfirm"
              defaultMessage="This will permanently delete the comment and any attachments. Continue?"
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteCandidateId(null)}
            disabled={deletingId !== null}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deletingId !== null}
          >
            <FormattedMessage id="delete" defaultMessage="Delete" />
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={droppedMentionNames.length > 0}
        autoHideDuration={8000}
        onClose={() => setDroppedMentionNames([])}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setDroppedMentionNames([])}>
          <FormattedMessage
            id="mentionNotNotified"
            defaultMessage="The comment was saved, but {names} could not be notified."
            values={{ names: droppedMentionNames.join(', ') }}
          />
        </Alert>
      </Snackbar>

      <Snackbar
        open={pasteError !== null}
        autoHideDuration={4000}
        onClose={() => setPasteError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setPasteError(null)}>
          {pasteError === 'tooLarge' ? (
            <FormattedMessage
              id="pasteImageTooLarge"
              defaultMessage="Pasted image exceeds 100 MB limit"
            />
          ) : (
            <FormattedMessage
              id="pasteImageUploadFailed"
              defaultMessage="Failed to upload pasted image"
            />
          )}
        </Alert>
      </Snackbar>
    </Card>
  );
}

export default CommentThread;
